/**
 * Tiered violation visualization — routes each violation to the lightest
 * correct overlay (PSET pattern): property arrows for min/max checks,
 * ray-march rects only for spatial rules (BCA 5.1.3). Works on 2D and 3D.
 */
import type { RuleSpec } from "@vera/rules";
import type { Violation } from "@vera/shared";
import { resolveViewerDbId } from "../lib/resolveViewerDbId";
import {
  evaluatePredicateChecks,
  overlayChipText,
  ruleVerdictFromChecks,
} from "../lib/rulePredicateChecks";
import { latestUserFactsForElementRule } from "../lib/userFactAuditDb";
import { overridesForRule } from "../lib/userFactScope";
import { useEngineStore } from "../stores/engineStore";
import { useFactPreviewStore } from "../stores/factPreviewStore";
import { geometryRowForDbId } from "../stores/geometryStore";
import { useProjectStore } from "../stores/projectStore";
import {
  mergeElementWithUserFacts,
  useUserFactStore,
} from "../stores/userFactStore";
import {
  type GeometryOverlayExtensionApi,
  getGeometryOverlayExtension,
} from "../viewer/extensions/GeometryOverlayExtension";
import { BCA_513_RULE_ID } from "../viewer/geometry/types";
import { is2dViewer } from "../viewer/geometry/viewMode";
import { shouldUseBuildingContextFit } from "./violationFocus";

export interface QuantityViolationViz {
  kind: "minimum" | "maximum";
  observedMm: number;
  thresholdMm: number;
  axis: "horizontal" | "vertical";
  propertyLabel: string;
}

function isQuantity(v: unknown): v is { value: number; unit: string } {
  return (
    v != null &&
    typeof v === "object" &&
    "value" in v &&
    typeof (v as { value: unknown }).value === "number" &&
    "unit" in v
  );
}

function overlayTone(violation: Violation): "violation" | "uncertain" | "pass" {
  if (violation.provenance?.status === "confirmed_violation") {
    return "violation";
  }
  if (violation.provenance?.status === "indeterminate") {
    return "uncertain";
  }
  return "uncertain";
}

function overlayDetail(violation: Violation, rule?: RuleSpec): string {
  const sectionRef =
    violation.code.match(/§[\d.A-Za-z-]+/)?.[0] ?? violation.code;

  if (!rule || typeof violation.elementDbId !== "number") {
    return sectionRef;
  }

  const element = useEngineStore
    .getState()
    .elements.find(e => e.dbId === violation.elementDbId);
  if (!element) return sectionRef;

  const previewState = useFactPreviewStore.getState();
  let facts = element.facts;
  if (previewState.violationId === violation.id && previewState.previewFacts) {
    facts = previewState.previewFacts;
  } else {
    const projectId = useProjectStore.getState().currentProjectId;
    const allOverrides = useUserFactStore.getState().overrides;
    const revisions = latestUserFactsForElementRule(
      projectId,
      violation.elementDbId,
      rule.ruleId
    );
    const scoped = overridesForRule(
      violation.elementDbId,
      rule.ruleId,
      allOverrides,
      revisions
    );
    facts = mergeElementWithUserFacts(element, {
      [violation.elementDbId]: scoped,
    }).facts;
  }

  const checks = evaluatePredicateChecks(rule, facts);
  const verdict = ruleVerdictFromChecks(checks, {
    ruleId: rule.ruleId,
    dbId: element.dbId,
    status: violation.provenance?.status ?? "indeterminate",
    summary: violation.summary,
  } as Parameters<typeof ruleVerdictFromChecks>[1]);

  return overlayChipText(sectionRef, verdict);
}

function inferAxis(
  rule: RuleSpec | undefined,
  propertyIri: string
): "horizontal" | "vertical" {
  const p = propertyIri.toLowerCase();
  if (
    /sill|height|riser|handrail|guard|headroom|openingheight|verticalrise/.test(
      p
    )
  ) {
    return "vertical";
  }
  if (/width|tread|landing|projection|clearance/.test(p)) {
    return "horizontal";
  }
  if (rule?.appliesTo.elementClass === "vera:Opening") return "vertical";
  if (rule?.appliesTo.elementClass === "vera:Door") return "horizontal";
  return "horizontal";
}

export function quantityVizFromViolation(
  violation: Violation,
  rule: RuleSpec | undefined
): QuantityViolationViz | null {
  const obs = violation.provenance?.observed;
  const thr = violation.provenance?.threshold;
  if (!isQuantity(obs) || !isQuantity(thr)) return null;
  if (obs.unit !== "millimetre" && thr.unit !== "millimetre") return null;

  const predicate = rule?.predicate;
  if (
    !predicate ||
    (predicate.kind !== "minimum" && predicate.kind !== "maximum")
  ) {
    return null;
  }

  return {
    kind: predicate.kind,
    observedMm: obs.value,
    thresholdMm: thr.value,
    axis: inferAxis(rule, predicate.property),
    propertyLabel: predicate.property.replace(/^vera:has/, ""),
  };
}

function findRuleForViolation(violation: Violation): RuleSpec | undefined {
  const ruleId = violation.provenance?.ruleId;
  if (!ruleId) return undefined;
  return useEngineStore.getState().rules.find(r => r.ruleId === ruleId);
}

async function clearOverlay(
  viewer: Autodesk.Viewing.GuiViewer3D
): Promise<GeometryOverlayExtensionApi | null> {
  const ext = await getGeometryOverlayExtension(viewer);
  ext?.clear();
  return ext;
}

/**
 * Visualize a violation on the active sheet (2D or 3D). Clears any prior
 * overlay first so only the selected violation is annotated.
 */
export async function visualizeViolation(
  viewer: Autodesk.Viewing.GuiViewer3D,
  violation: Violation
): Promise<boolean> {
  const extractionDbId = violation.elementDbId;
  if (typeof extractionDbId !== "number") return false;

  const element = useEngineStore
    .getState()
    .elements.find(e => e.dbId === extractionDbId);
  const dbId =
    resolveViewerDbId(viewer, extractionDbId, element?.displayName) ??
    extractionDbId;

  const ext = await clearOverlay(viewer);
  if (!ext) return false;

  const plan2d = is2dViewer(viewer);
  const rule = findRuleForViolation(violation);
  const tone = overlayTone(violation);
  const row = geometryRowForDbId(extractionDbId);

  if (rule?.ruleId === BCA_513_RULE_ID && row?.measure.geometry) {
    ext.drawMeasurement(row.measure, row.pass, { plan2d });
    return true;
  }

  const qty = quantityVizFromViolation(violation, rule);
  if (qty) {
    ext.drawQuantityCheck(dbId, { ...qty, codeLabel: violation.code, plan2d });
    return true;
  }

  if (violation.provenance?.observed && violation.provenance.threshold) {
    const elements = useEngineStore.getState().elements;
    const buildingContext =
      !plan2d && shouldUseBuildingContextFit(viewer, dbId, elements, violation);
    ext.drawPropertyViolation(dbId, "", overlayDetail(violation, rule), {
      plan2d,
      tone,
      buildingContext,
    });
    return true;
  }

  if (violation.provenance?.status === "indeterminate") {
    ext.drawPropertyViolation(
      dbId,
      element?.displayName ?? "",
      overlayDetail(violation, rule),
      { plan2d, tone: "uncertain" }
    );
    return true;
  }

  return false;
}
